/**
 * TODO: refactor to api calls and data normalization
 * possibly another function or another util file
 *
 * see: Single Responsibility Principle
 */

import { Toast } from '@zillow/constellation-10';
import { type TourConfirmationType } from '@zg-rentals/ts-z-calendar-client';

import { cloneDeep } from 'es-toolkit';

import PropertiesActions from '../../actions/PropertiesActions';
import RelistAdditionalFieldsActions from '../../actions/RelistActions';

import { multiFieldsUtil } from '../../utils/postingUtils';
import { getRelistingAdditionalFields } from '../../utils/relistingUtils';
import stringHelper from '../../utils/stringHelper';
import typeCheckUtils from '../../utils/typeCheckUtils';
import { getFormattedDateForDatePicker } from '../../utils/dateUtils';

import { APPLICATIONS_ALLOWED_BY_FOR_RENT_BY_KEY, FOR_RENT_BY_KEY, ROOM_FOR_RENT_FLOW, STEP } from './constants';

import type { FloorPlanProps } from '../../model/FloorPlan';
import type { CurrentPropertyProps } from '../../model/CurrentProperty';
import { logError } from '@zg-rentals/log-error';
import type { UserPropertyMultiEditRequestWebDto } from '@zg-rentals/ts-rental-manager-api-beta-client';

type Action = {
  type: string;
  payload: Record<string, unknown>;
};

type ReducerDispatch = (action: Action) => void;

type ActionFunction = (args: { reducerDispatch: ReducerDispatch; data: Record<string, unknown> }) => void;

type HasContentObjType = {
  renterAgeAndGenderPreferred?: boolean;
  roommateCount?: boolean;
  roommateDetailsDescription?: boolean;
  roommateTotalCount?: boolean;
};

const ROOMMATE_COUNT_DISPLAY_MAP = {
  roommateMaleCount: 'male',
  roommateFemaleCount: 'female',
  roommateNonBinaryCount: 'non-binary',
  roommatePreferNotToIdentifyCount: 'prefer not to identify',
  roommateTotalCount: 'count',
};

const CURRENT_PETS_DISPLAY_MAP = {
  currentCatsCount: (value: string) => {
    return stringHelper.pluralize(value, 'cat', 'cats');
  },
  currentDogsCount: (value: string) => {
    return stringHelper.pluralize(value, 'dog', 'dogs');
  },
};

const checkIsApplicationAcceptedValueChanging = ({
  isApplicationsAccepted,
  nextForRentByVal,
}: {
  isApplicationsAccepted: boolean;
  nextForRentByVal: string;
}) => {
  const nextIsApplicationsAccepted = APPLICATIONS_ALLOWED_BY_FOR_RENT_BY_KEY[nextForRentByVal];
  const isApplicationAcceptedValueChanging = isApplicationsAccepted !== nextIsApplicationsAccepted;
  const isApplicationsAcceptedChangingToTrueValue =
    isApplicationAcceptedValueChanging && nextIsApplicationsAccepted === true;

  return {
    isApplicationAcceptedValueChanging,
    isApplicationsAcceptedChangingToTrueValue,
    nextIsApplicationsAccepted,
  };
};

/**
 * Checks against current room for rent field data when
 * the room for rent flow is changing personas.
 *
 * roomForRentFlow - the current context state of the room for rent flow
 */
const checkDisplayForRentByChangeWarning = ({
  roomForRentFlow,
  roomForRent,
}: {
  roomForRentFlow: string;
  roomForRent: Record<string, Record<string, string | Array<string>>>;
}) => {
  const {
    currentRoommatesDetails: {
      roommateFemaleCount,
      roommateMaleCount,
      roommateNonBinaryCount,
      roommatePreferNotToIdentifyCount,
      roommateTotalCount,
      roommateDetailsDescription,
    },
    roommateRequirements: { roommateGenderPreferences, roommateMinAgePreference },
  } = roomForRent;

  let displayWarning = false;
  const hasContentObj: HasContentObjType = {};

  if (roomForRentFlow === ROOM_FOR_RENT_FLOW.IRENE_FLOW) {
    if (roommateTotalCount !== '') {
      displayWarning = true;
      hasContentObj['roommateTotalCount'] = true;
    }
  }

  if (roomForRentFlow === ROOM_FOR_RENT_FLOW.RYAN_FLOW) {
    const hasRoommateCountContent =
      roommateFemaleCount !== '' ||
      roommateMaleCount !== '' ||
      roommateNonBinaryCount !== '' ||
      roommatePreferNotToIdentifyCount !== '';

    const hasRoommateMinAgeAndGenderPreferenceContent =
      roommateGenderPreferences.length > 0 || roommateMinAgePreference !== null;

    if (hasRoommateCountContent) {
      displayWarning = true;
      hasContentObj['roommateCount'] = true;
    }

    if (roommateDetailsDescription !== '') {
      displayWarning = true;
      hasContentObj['roommateDetailsDescription'] = true;
    }

    if (hasRoommateMinAgeAndGenderPreferenceContent) {
      displayWarning = true;
      hasContentObj['renterAgeAndGenderPreferred'] = true;
    }
  }

  return {
    displayWarning,
    hasContentObj,
  };
};

/**
 * when forRentBy is owner or agent - irene flow
 * when forRentBy is ownerResident or tenant - ryan flow
 */
const getRoomForRentFlowObj = (forRentBy: string) => {
  const isRyanFlow = forRentBy === FOR_RENT_BY_KEY.OWNER_RESIDENT || forRentBy === FOR_RENT_BY_KEY.TENANT;
  const isIreneFlow = forRentBy === FOR_RENT_BY_KEY.OWNER || forRentBy === FOR_RENT_BY_KEY.AGENT;

  return { isRyanFlow, isIreneFlow };
};

const getRoomForRentFlow = (forRentBy: string) => {
  const { isRyanFlow, isIreneFlow } = getRoomForRentFlowObj(forRentBy);

  let response = null;

  if (isRyanFlow) {
    response = ROOM_FOR_RENT_FLOW.RYAN_FLOW;
  }

  if (isIreneFlow) {
    response = ROOM_FOR_RENT_FLOW.IRENE_FLOW;
  }

  return response;
};

const getGenderPreferencesDisplay = (roommateGenderPreferences: Array<string>) => {
  const kebabList = roommateGenderPreferences.map((string) => {
    return stringHelper.kebabize(string);
  });

  const genderPreferred = kebabList.length === 2 ? `${kebabList[0]} or ${kebabList[1]}` : kebabList.join(', ');

  return genderPreferred;
};

const getRoommateCountDisplay = ({ currentRoommatesDetails }: { currentRoommatesDetails: Record<string, string> }) => {
  const displayArr: Array<string> = [];

  // loop through DISPLAY_MAP to specify display order
  Object.keys(ROOMMATE_COUNT_DISPLAY_MAP).forEach((key) => {
    const value = currentRoommatesDetails[key];

    // accepts a zero (0) value
    if (value !== '' && value !== null) {
      // @ts-expect-error
      const displayItem = `${value} ${ROOMMATE_COUNT_DISPLAY_MAP[key]}`;
      displayArr.push(displayItem);
    }
  });

  return displayArr.join(', ');
};

const getRoomCurrentPetsDisplay = ({ currentPets }: { currentPets: Record<string, string> }) => {
  const displayArr: Array<string> = [];

  // loop through DISPLAY_MAP to specify display order
  Object.keys(CURRENT_PETS_DISPLAY_MAP).forEach((key) => {
    const value = currentPets[key];

    // accepts a zero (0) value
    if (value !== '' && value !== null) {
      const containsPlus = value.includes('+');

      const newValue = containsPlus ? value.replace('+', '') : value;

      // @ts-expect-error
      let displayItem = CURRENT_PETS_DISPLAY_MAP[key](Number(newValue));

      if (containsPlus) {
        displayItem = displayItem.replace(`${newValue} `, `${newValue}+ `);
      }

      displayArr.push(displayItem);
    }
  });

  return displayArr.join(', ');
};

const handleApplicationsStepLogicChange = ({
  addToWizardConfig,
  canAccessFastPost,
  isApplicationsUpdateRestricted,
  removeFromWizardConfig,
}: {
  addToWizardConfig: (stepKey: string) => void;
  canAccessFastPost: boolean;
  isApplicationsUpdateRestricted: boolean;
  removeFromWizardConfig: (stepKey: string) => void;
}) => {
  if (isApplicationsUpdateRestricted || canAccessFastPost) {
    removeFromWizardConfig(STEP.ACCEPT_ZILLOW_APPLICATIONS);

    return;
  }

  addToWizardConfig(STEP.ACCEPT_ZILLOW_APPLICATIONS);
};

const handleForRentByChange = ({
  forRentBy,
  removeFromWizardConfig,
  addToWizardConfig,
}: {
  forRentBy: string;
  removeFromWizardConfig: (stepKey: string) => void;
  addToWizardConfig: (stepKey: string) => void;
}) => {
  if (forRentBy === FOR_RENT_BY_KEY.AGENT) {
    // management companies can not hide phone numbers
    removeFromWizardConfig(STEP.ALLOW_CONTACT_BY_PHONE);
  } else {
    addToWizardConfig(STEP.ALLOW_CONTACT_BY_PHONE);
  }
};

const handleRoomForRentFlowChange = ({
  isRoomForRent,
  roomForRentFlow,
  removeFromWizardConfig,
  addToWizardConfig,
}: {
  isRoomForRent: boolean;
  roomForRentFlow: string;
  removeFromWizardConfig: (stepKey: string) => void;
  addToWizardConfig: (stepKey: string) => void;
}) => {
  if (isRoomForRent === false) {
    return;
  }

  // TODO: handle case where there has not been a flow chosen
  if (roomForRentFlow === ROOM_FOR_RENT_FLOW.IRENE_FLOW) {
    removeFromWizardConfig(STEP.ROOMMATE_DESCRIPTION);
    removeFromWizardConfig(STEP.RENTER_PREFERENCES);
  }

  if (roomForRentFlow === ROOM_FOR_RENT_FLOW.RYAN_FLOW) {
    addToWizardConfig(STEP.ROOMMATE_DESCRIPTION);
    addToWizardConfig(STEP.RENTER_PREFERENCES);
  }
};

const handleRoomForRentSteps = ({
  isRoomForRent,
  removeFromWizardConfig,
}: {
  isRoomForRent: boolean;
  removeFromWizardConfig: (stepKey: string) => void;
}) => {
  if (isRoomForRent) {
    // this step will be replace with STEP.ROOM_DESCRIPTION
    removeFromWizardConfig(STEP.DESCRIBE_PROPERTY);

    return;
  }

  // no access to room for rent: remove all related steps
  removeFromWizardConfig(STEP.ROOM_WHO_IS_LISTING_PROPERTY);
  removeFromWizardConfig(STEP.ROOM_DESCRIPTION);
  removeFromWizardConfig(STEP.ROOM_HOW_MANY_ROOMMATES);
  removeFromWizardConfig(STEP.ROOMMATE_DESCRIPTION);
  removeFromWizardConfig(STEP.RENTER_PREFERENCES);
  removeFromWizardConfig(STEP.ROOM_CURRENT_PETS);
  removeFromWizardConfig(STEP.ROOM_UTILITIES_AMOUNT);
};

const LEASE_DURATION_FAST_POST_DEFAULT_VALUE = '1y';

const handleFastPostSteps = ({
  canAccessFastPost,
  currentProperty: {
    floorplans,
    listingAlias,
    hideAddress,
    leaseDuration,
    hidePhone,
    isApplicationsAccepted,
    isMultifamily,
    restrictions,
  } = {},
  dispatch = () => {},
  saveMultipleFieldsPropertyInfo,
  enqueueToast = () => {},
  addToWizardConfig = () => {},
  removeFromWizardConfig = () => {},
}: {
  canAccessFastPost: boolean;
  currentProperty: Partial<CurrentPropertyProps>;
  dispatch: () => void;
  saveMultipleFieldsPropertyInfo: (data: UserPropertyMultiEditRequestWebDto) => Promise<unknown>;
  enqueueToast: (component: JSX.Element, options: Record<string, unknown>) => void;
  removeFromWizardConfig: (stepKey: string) => void;
  addToWizardConfig: (stepKey: string) => void;
}) => {
  /**
   * NOTE: safeguard for MF
   * MF does not have any config for steps so no need to handle the same as `canAccessFastPost`
   */
  if (isMultifamily) {
    return;
  }

  if (!canAccessFastPost) {
    removeFromWizardConfig(STEP.FAST_POST_REVIEW);

    return;
  }

  addToWizardConfig(STEP.FAST_POST_REVIEW);

  removeFromWizardConfig(STEP.HIDE_PROPERTY_ADDRESS);
  removeFromWizardConfig(STEP.WHEN_IS_PROPERTY_AVAILABLE);
  removeFromWizardConfig(STEP.LEASE_DURATION);
  removeFromWizardConfig(STEP.ALLOW_CONTACT_BY_PHONE);

  // NOTE: server time is in EST/EDT
  const todayEasternTime = new Date(new Date(Date.now()).toLocaleString('en-US', { timeZone: 'America/New_York' }));
  const formattedDate = getFormattedDateForDatePicker({
    initialDate: floorplans?.[0]?.dateAvailable,
    today: todayEasternTime,
    dateFormat: 'yyyy-MM-dd',
  });

  const dataToSave: UserPropertyMultiEditRequestWebDto = {
    property: {
      listingAlias: listingAlias ?? '',
    },
  };

  if (typeCheckUtils.Boolean(hideAddress) === null) {
    dataToSave.property['hideStreetAddress'] = false;
  }

  let copyFloorplan: Array<FloorPlanProps> | null = null;

  if (!floorplans?.[0]?.floorPlanId) {
    logError({
      error: new Error('ERROR@ MISSING — floor plans?.[0]?.floorPlanId'),
      errorType: 'FloorPlanError',
      context: {
        floorplans,
        listingAlias,
        url: typeof window !== 'undefined' ? location.href : 'NO WINDOW',
      },
    });
  }

  if (floorplans?.[0]?.floorPlanId && floorplans?.[0]?.dateAvailable !== formattedDate) {
    dataToSave.floorPlans = [
      {
        dateAvailable: formattedDate,
        id: floorplans[0].floorPlanId,
      },
    ];

    copyFloorplan = cloneDeep(floorplans) as Array<FloorPlanProps>;
    copyFloorplan[0].dateAvailable = formattedDate;
  }

  if (!leaseDuration) {
    dataToSave.property.leaseDuration = LEASE_DURATION_FAST_POST_DEFAULT_VALUE;
  }

  if (typeCheckUtils.Boolean(hidePhone) === null) {
    dataToSave.property.hidePhone = false;
  }

  if (typeCheckUtils.Boolean(isApplicationsAccepted) === null && !restrictions?.applications?.isUpdateRestricted) {
    dataToSave.property.isApplicationsAccepted = true;
  }

  const keysToSave = Object.keys(dataToSave.property);

  if (keysToSave.length > 1 || copyFloorplan) {
    saveMultipleFieldsPropertyInfo(dataToSave)
      .then((response: unknown) => {
        const errorMessage = multiFieldsUtil.getMultiEditErrorMessageFromResponse(response);
        if (errorMessage) {
          enqueueToast(<Toast tone="critical">{errorMessage}</Toast>, { duration: 10000 });
          return;
        }
        const newCurrentProperty = Object.entries({
          hideAddress: dataToSave.property.hideStreetAddress,
          floorplans: copyFloorplan,
          leaseDuration: dataToSave.property.leaseDuration,
          hidePhone: dataToSave.property.hidePhone,
          isApplicationsAccepted: dataToSave.property.isApplicationsAccepted,
        }).reduce((a: Record<string, unknown>, [k, v]) => (v === undefined || v === null ? a : ((a[k] = v), a)), {});

        // @ts-expect-error
        dispatch(PropertiesActions.updateCurrentProperty(newCurrentProperty));
      })
      .catch(() => {
        enqueueToast(<Toast tone="critical">Oops! Something went wrong. Please reload the page.</Toast>, {
          duration: 10000,
        });
      });
  }
};

const handleRelistingSteps = ({
  dispatch,
  saveMultipleFieldsPropertyInfo,
  canAccessRelistingFlow,
  addToWizardConfig,
  removeFromWizardConfig,
  currentProperty,
  isRoomForRent,
  toursData,
  canAccessSFCostsAndFees,
  enqueueToast = () => {},
}: {
  dispatch: () => void;
  saveMultipleFieldsPropertyInfo: (data: UserPropertyMultiEditRequestWebDto) => Promise<unknown>;
  canAccessRelistingFlow: boolean;
  addToWizardConfig: (stepKey: string) => void;
  removeFromWizardConfig: (stepKey: string) => void;
  currentProperty: Record<string, Record<string, unknown>>;
  isRoomForRent: boolean;
  toursData: Record<string, unknown>;
  canAccessSFCostsAndFees: boolean;
  enqueueToast: (component: JSX.Element, options: Record<string, unknown>) => void;
}) => {
  if (canAccessRelistingFlow) {
    const [fieldsToRender, renderAdditionalDetailsPage] = getRelistingAdditionalFields(
      // @ts-expect-error
      currentProperty,
      isRoomForRent,
      toursData,
      canAccessSFCostsAndFees,
    );

    // @ts-expect-error
    dispatch(RelistAdditionalFieldsActions.updateRelistAdditionalFields(fieldsToRender, renderAdditionalDetailsPage));

    //  if no additional details fields are empty, remove this page
    if (!renderAdditionalDetailsPage) {
      removeFromWizardConfig(STEP.CONSIDER_ADDITIONAL_DETAILS);
      return;
    }

    const {
      listingAlias,
      modularListingProgress: { step },
    } = currentProperty;
    // if current step is the review step (without ::relisting suffix) it's user's first visit to relisting
    // reset the applications, hide address, hide phone fields to the "recommended" value
    if (step === STEP.REVIEW.toLowerCase()) {
      const dataToSave: UserPropertyMultiEditRequestWebDto = {
        property: {
          listingAlias: listingAlias as unknown as string,
        },
      };

      if (fieldsToRender.renderHidePropertyAddressField === true) {
        dataToSave.property.hideStreetAddress = false;
      }

      if (fieldsToRender.renderAllowContactByPhoneField === true) {
        dataToSave.property.hidePhone = false;
      }

      if (fieldsToRender.renderAcceptApplicationsField === true) {
        dataToSave.property.isApplicationsAccepted = true;
      }

      const keysToSave = Object.keys(dataToSave.property);

      if (keysToSave.length > 1) {
        saveMultipleFieldsPropertyInfo(dataToSave)
          .then((response: unknown) => {
            const errorMessage = multiFieldsUtil.getMultiEditErrorMessageFromResponse(response);
            if (errorMessage) {
              enqueueToast(<Toast tone="critical">{errorMessage}</Toast>, { duration: 10000 });
              return;
            }
            const newCurrentProperty = Object.entries({
              hideAddress: dataToSave.property.hideStreetAddress,
              hidePhone: dataToSave.property.hidePhone,
              isApplicationsAccepted: dataToSave.property.isApplicationsAccepted,
            }).reduce(
              (a: Record<string, unknown>, [k, v]) => (v === undefined || v === null ? a : ((a[k] = v), a)),
              {},
            );

            // @ts-expect-error
            dispatch(PropertiesActions.updateCurrentProperty(newCurrentProperty));
          })
          .catch(() => {
            enqueueToast(<Toast tone="critical">Oops! Something went wrong. Please reload the page.</Toast>, {
              duration: 10000,
            });
          });
      }
    }

    addToWizardConfig(STEP.UPDATE_RECENTLY_CHANGED);
    addToWizardConfig(STEP.CONSIDER_ADDITIONAL_DETAILS);

    return;
  }

  // no access to relisting flow: remove all related steps
  removeFromWizardConfig(STEP.UPDATE_RECENTLY_CHANGED);
  removeFromWizardConfig(STEP.CONSIDER_ADDITIONAL_DETAILS);
};

const updateRoomForRentFlowContextByForRentBy = ({
  forRentBy,
  actions,
  reducerDispatch,
}: {
  forRentBy: string;
  actions: Record<string, ActionFunction>;
  reducerDispatch: ReducerDispatch;
}) => {
  const roomForRentFlow = getRoomForRentFlow(forRentBy);

  actions.updateAnyProperty({
    reducerDispatch,
    data: {
      roomForRentFlow,
    },
  });
};

const handleZRMCostsAndFeesSteps = ({
  canAccessSFCostsAndFees,
  removeFromWizardConfig,
  addToWizardConfig,
}: {
  canAccessSFCostsAndFees: boolean;
  removeFromWizardConfig: (stepKey: string) => void;
  addToWizardConfig: (stepKey: string) => void;
}) => {
  if (canAccessSFCostsAndFees) {
    addToWizardConfig(STEP.COSTS_AND_FEES);

    return;
  }

  removeFromWizardConfig(STEP.COSTS_AND_FEES);
};

const removeTourSchedulingNewListingSteps = (removeFromWizardConfig: (stepKey: string) => void) => {
  removeFromWizardConfig(STEP.TOUR_SCHEDULING_BOOKING_METHOD);
  removeFromWizardConfig(STEP.TOUR_SCHEDULING_TIMEZONE);
  removeFromWizardConfig(STEP.TOUR_SCHEDULING_AVAILABILITY);
};

const removeTourSchedulingRelistSteps = (removeFromWizardConfig: (stepKey: string) => void) => {
  removeFromWizardConfig(STEP.TOUR_SCHEDULING_BOOKING_METHOD_RELIST);
  removeFromWizardConfig(STEP.TOUR_SCHEDULING_TIMEZONE_RELIST);
  removeFromWizardConfig(STEP.TOUR_SCHEDULING_AVAILABILITY_RELIST);
};

const handlePostingPathTourSchedulingSteps = ({
  canAccessPostingPathTourScheduling,
  addToWizardConfig,
  removeFromWizardConfig,
  canAccessRelistingFlow,
  selectedBookingMethod,
  initialZoneIdWithoutTourConfig,
}: {
  canAccessPostingPathTourScheduling: boolean;
  addToWizardConfig: (stepKey: string) => void;
  removeFromWizardConfig: (stepKey: string) => void;
  canAccessRelistingFlow: boolean;
  selectedBookingMethod?: TourConfirmationType;
  initialZoneIdWithoutTourConfig?: string;
}) => {
  if (canAccessPostingPathTourScheduling) {
    if (canAccessRelistingFlow) {
      addToWizardConfig(STEP.TOUR_SCHEDULING_BOOKING_METHOD_RELIST);
      removeTourSchedulingNewListingSteps(removeFromWizardConfig);

      if (selectedBookingMethod && initialZoneIdWithoutTourConfig) {
        removeFromWizardConfig(STEP.TOUR_SCHEDULING_TIMEZONE_RELIST);
        addToWizardConfig(STEP.TOUR_SCHEDULING_AVAILABILITY_RELIST);
      } else if (selectedBookingMethod) {
        addToWizardConfig(STEP.TOUR_SCHEDULING_TIMEZONE_RELIST);
        addToWizardConfig(STEP.TOUR_SCHEDULING_AVAILABILITY_RELIST);
      } else {
        removeFromWizardConfig(STEP.TOUR_SCHEDULING_TIMEZONE_RELIST);
        removeFromWizardConfig(STEP.TOUR_SCHEDULING_AVAILABILITY_RELIST);
      }
    } else {
      addToWizardConfig(STEP.TOUR_SCHEDULING_BOOKING_METHOD);
      removeTourSchedulingRelistSteps(removeFromWizardConfig);

      if (selectedBookingMethod) {
        addToWizardConfig(STEP.TOUR_SCHEDULING_TIMEZONE);
        addToWizardConfig(STEP.TOUR_SCHEDULING_AVAILABILITY);
      } else {
        removeFromWizardConfig(STEP.TOUR_SCHEDULING_TIMEZONE);
        removeFromWizardConfig(STEP.TOUR_SCHEDULING_AVAILABILITY);
      }
    }
  } else {
    removeTourSchedulingNewListingSteps(removeFromWizardConfig);
    removeTourSchedulingRelistSteps(removeFromWizardConfig);
  }
};

const newListingTourSchedulingRedirectSteps = [STEP.TOUR_SCHEDULING_TIMEZONE, STEP.TOUR_SCHEDULING_AVAILABILITY];
const relistingTourSchedulingRedirectSteps = [
  STEP.TOUR_SCHEDULING_TIMEZONE_RELIST,
  STEP.TOUR_SCHEDULING_AVAILABILITY_RELIST,
];

const isTourSchedulingRedirectStep = (step: string) => {
  const tourSchedulingRedirectSteps = [
    ...newListingTourSchedulingRedirectSteps,
    ...relistingTourSchedulingRedirectSteps,
  ];

  return tourSchedulingRedirectSteps.includes(step?.toUpperCase() as (typeof tourSchedulingRedirectSteps)[number]);
};

const getTourSchedulingRedirectStep = (step: string) => {
  if (
    newListingTourSchedulingRedirectSteps.includes(
      step.toUpperCase() as (typeof newListingTourSchedulingRedirectSteps)[number],
    )
  ) {
    return STEP.TOUR_SCHEDULING_BOOKING_METHOD;
  }

  if (
    relistingTourSchedulingRedirectSteps.includes(
      step.toUpperCase() as (typeof relistingTourSchedulingRedirectSteps)[number],
    )
  ) {
    return STEP.TOUR_SCHEDULING_BOOKING_METHOD_RELIST;
  }

  return null;
};

export {
  checkIsApplicationAcceptedValueChanging,
  checkDisplayForRentByChangeWarning,
  getGenderPreferencesDisplay,
  getRoomCurrentPetsDisplay,
  getRoomForRentFlow,
  getRoomForRentFlowObj,
  getRoommateCountDisplay,
  handleApplicationsStepLogicChange,
  handleForRentByChange,
  handleRoomForRentFlowChange,
  handleRoomForRentSteps,
  handleFastPostSteps,
  handleRelistingSteps,
  updateRoomForRentFlowContextByForRentBy,
  handleZRMCostsAndFeesSteps,
  handlePostingPathTourSchedulingSteps,
  isTourSchedulingRedirectStep,
  getTourSchedulingRedirectStep,
};
